import React, { useState, useEffect } from 'react';
import { deleteUser, updateProfile, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useUserAuth } from '../context/UserAuthContext';
import { db, auth } from '../../config/firebase';
import { collection, query, where, getDocs, limit, orderBy, writeBatch } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import toast from "react-hot-toast";
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as Dialog from '@radix-ui/react-dialog';
import { profile_banner } from '../../common/links';

function Profile() {
  const { user, logOut } = useUserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [files, setFiles] = useState([]);

  // Edit Profile States
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newName, setNewName] = useState(user?.displayName || '');
  const [newPhotoURL, setNewPhotoURL] = useState(user?.photoURL || '');

  // Delete Account States
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    setNewName(user?.displayName || '');
    setNewPhotoURL(user?.photoURL || '');
  }, [user]);

  useEffect(() => {
    const fetchImages = async () => {
      if (!user?.email) return;
      setLoading(true);
      try {
        const q = query(
          collection(db, "files"),
          where("userEmail", "==", user.email),
          orderBy("timeCreated", "desc"),
          limit(4)
        );
        const querySnapshot = await getDocs(q);
        const fetchedFiles = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFiles(fetchedFiles);
      } catch (error) {
        console.log('Error fetching images:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsBtnLoading(true);
    const toastId = toast.loading("Updating profile...");
    try {
      await updateProfile(auth.currentUser, {
        displayName: newName,
        photoURL: newPhotoURL
      });
      toast.success("Profile updated successfully!", { id: toastId });
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update profile: " + error.message, { id: toastId });
    } finally {
      setIsBtnLoading(false);
    }
  };

  const handleFirebaseError = (error) => {
    toast.error(error.message.replace('Firebase: Error ', '').replace(/[()]/g, '').replace('.', ''), {
      position: "bottom-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
    console.error(error);
  };

  return (
    <div className='w-full md:h-screen md:max-h-screen max-h-[calc(100vh-100px)] h-[90vh] bg-white md:bg-gray-200 flex items-center justify-center'>
      <div className="relative flex flex-col profile overflow-y-scroll overflow-x-hidden items-center md:rounded-[20px] w-full md:mx-5 h-[85vh] md:h-[95vh] p-4 bg-white bg-clip-border md:shadow-lg shadow-gray-300">
        <div className="relative flex min-h-[200px] md:min-h-[250px] md:h-[250px]  w-full justify-center rounded-xl bg-cover">
          <img src={profile_banner} className="absolute flex h-full w-full justify-center  rounded-xl brightness-75 bg-gray-300" />
          <h4 className="text-2xl lg:text-4xl uppercase hidden break-all md:block z-20 absolute bottom-2 left-[150px] font-bold text-white ">
            {user.displayName}
          </h4>

          <div className="z-20 absolute top-3 right-3">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="px-3 py-2 bg-[#98d6ed] border-1 border-[#80cde8] hidden md:block text-gray-800 text-md font-semibold font-display rounded-lg">
              Action <i className="fas mt-1 fa-angle-down"></i>
            </button>
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="px-3.5 py-2 md:hidden bg-blue-100 text-blue-500 text-md font-semibold font-display rounded-full active:scale-95 transition-all">
              <i className="fa-solid fa-ellipsis mt-1 rotate-90"></i>
            </button>
            {isDropdownOpen && (
              <div className="origin-top-right z-30 absolute right-0 mt-2 w-48 px-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                  <Dialog.Root open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                    <Dialog.Trigger asChild>
                      <div
                        className="block w-full text-start px-4 cursor-pointer rounded-md py-2 text-sm mt-1  hover:bg-blue-200"
                        role="menuitem"
                      >
                        Edit Profile
                      </div>
                    </Dialog.Trigger>
                    <Dialog.Portal>
                      <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" />
                      <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white p-6 rounded-2xl shadow-xl z-50">
                        <Dialog.Title className="text-xl font-bold mb-4">Edit Profile</Dialog.Title>
                        <form onSubmit={handleUpdateProfile} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                            <input 
                              type="text" 
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="Your Name"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Avatar URL</label>
                            <input 
                              type="url" 
                              value={newPhotoURL}
                              onChange={(e) => setNewPhotoURL(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="https://example.com/photo.jpg"
                            />
                            {newPhotoURL && (
                              <div className="mt-4 flex flex-col items-center">
                                <span className="text-xs text-gray-400 mb-2 uppercase font-bold">Preview</span>
                                <div className="h-24 w-24 rounded-full border-4 border-blue-50 overflow-hidden shadow-sm">
                                  <img 
                                    src={newPhotoURL} 
                                    alt="Preview" 
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL';
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex justify-end gap-3 mt-6">
                            <Dialog.Close asChild>
                              <button type="button" className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                            </Dialog.Close>
                            <button 
                              type="submit" 
                              disabled={isBtnLoading}
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                            >
                              {isBtnLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </form>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>

                  <button
                    onClick={(e) => {
                      localStorage.removeItem('token');
                      logOut();
                      window.location.href = '/home';
                    }}
                    className=" w-full text-start px-4 rounded-md py-2 text-sm my-1  hover:bg-blue-200 inline-flex items-center gap-2"
                    role="menuitem"
                  >
                    Sign out <i className="fas fa-arrow-right-from-bracket text-gray-500"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="absolute -bottom-12 md:-bottom-13 md:left-[30px] flex h-28 w-28 items-center justify-center rounded-full border-[4px] border-white ">
            <img className="h-full w-full rounded-full" src={user.photoURL} alt="" />
            {user.emailVerified && <img src={require('../assert/verified.png')} alt="verified" className="absolute -bottom-1 right-0.5 w-7 rounded-full " />}
          </div>
        </div>

        <div className="mt-16 md:ml-10 md:w-full flex flex-col justify-center md:items-start items-center ">
          <h4 className="text-xl break-all hidden md:inline-flex items-center font-bold text-navy-700 ">{user.uid} <span className="bg-gray-200 ml-2 px-2 py-1 text-sm rounded-xl text-gray-500">userid</span> </h4>
          <h4 className="text-xl break-words text-center md:hidden font-bold text-navy-700 ">{user.displayName}</h4>
          <h4 className="text-xl text-center break-all md:hidden mt-3 font-bold text-navy-700 ">{user.uid} </h4>
          <p className="text-lg lg:ms-2 break-all font-normal mt-1 text-gray-600">{user.email}</p>
        </div>

        {loading ? (
          <div className="flex w-full items-center justify-center h-[40vh] pb-4" >
            <svg className={`animate-spin h-10 w-10 text-[#00a1e1]`} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (files.length > 0 ? (
          <div className="mt-10 h-[40vh] w-full">
            <h1 className="text-xl hidden md:block font-semibold ms-5">Recent Files</h1>
            <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 mt-3 ">
              {files.slice(0, 4).map((data) => (
                <div 
                  className="p-4 cursor-pointer" 
                  key={data.id}
                  onClick={() => navigate(`/dashboard/myspace?previewId=${data.id}`)}
                >
                  <div className={`h-52 w-[1fr] bg-gray-500 relative rounded-lg overflow-hidden shadow-lg hover:scale-[1.02] transition-transform`}>
                    <img src={data.url} alt="" className="w-full h-full object-cover relative brightness-[58%]" />
                    <span className="absolute md:left-[5%] mx-5 text-gray-50 top-[35%] pb-3">
                      <h1 className="inline-flex break-all items-center pe-3 gap-2 md:text-2xl text-3xl font-bold mb-3">{data.name.length > 20 ? `${data.name.slice(0, 20)}...` : `${data.name}.${data.contentType.replace("image/",'')}`} </h1>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center py-5 my-5 justify-center flex-col">
            <img src='https://user-images.githubusercontent.com/507615/54591670-ac0a0180-4a65-11e9-846c-e55ffce0fe7b.png' alt="NO API KEY" className="w-44" />
            <p className="text-base">Not Yet Uploaded Anything...</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Profile;
